import type {
  PortfolioAnalyticsPropertyDto,
  PortfolioAnalyticsStatsDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { ClientCpmBoostType, CustomerAnalyticsBoostMediaChannel } from '@zg-rentals/ts-customer-analytics-client';
import type { ReactElement } from 'react';
import { Box, Icon, Paragraph, Popover, Spinner, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconCheckmarkFilled, IconCloseFilled } from '@zillow/constellation-icons';
import { formatNumber } from '../../../../utils';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { mapPackageTypeName } from '../../../../utils/mapPackageTypeName';
import { formatSpotlightDates } from '../../../../utils/formatSpotlightDates';
import { MarketTrendsDownloadCell } from './MarketTrendsDownloadCell';

const getCpmBoostPlatformLabel = (cpmBoostTypes: Array<ClientCpmBoostType>): string => {
  const hasMeta = cpmBoostTypes.some(
    (type) =>
      type === ClientCpmBoostType.FACEBOOK ||
      type === ClientCpmBoostType.INSTAGRAM ||
      type === ClientCpmBoostType.INSTAGRAM_STORY,
  );
  const hasZg = cpmBoostTypes.some((type) => type === ClientCpmBoostType.ZG);

  if (hasMeta && hasZg) return 'Meta, Zillow';
  if (hasMeta) return 'Meta';
  if (hasZg) return 'Zillow';
  if (cpmBoostTypes.length > 0) return cpmBoostTypes.join(', ');
  return '-';
};

const getExtendPlatformLabel = (data: StatType): string => {
  if (!data.addOnMediaChannels || data.addOnMediaChannels.length === 0) return '-';
  const hasMeta = data.addOnMediaChannels.includes(CustomerAnalyticsBoostMediaChannel.META);
  const hasTikTok = data.addOnMediaChannels.includes(CustomerAnalyticsBoostMediaChannel.TIKTOK);

  if (hasMeta && hasTikTok) return 'Meta, TikTok';
  if (hasMeta) return 'Meta';
  if (hasTikTok) return 'TikTok';
  return '-';
};

type InferArrayType<T> = T extends Array<infer U> ? U : never;

export enum TableType {
  ALL = 'all',
  PACKAGES = 'packages',
  BOOST = 'boost',
  EXTEND = 'extend',
}

export type MonthlyPropertyStats = {
  stats: Array<PortfolioAnalyticsStatsDto & { propertyDetails: PortfolioAnalyticsPropertyDto }>;
  monthAndYear: string;
};

export type StatType = InferArrayType<MonthlyPropertyStats['stats']>;

export interface AdSpendTableConfig {
  field?: keyof StatType;
  header: string;
  align: 'left' | 'right' | 'center';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderer: (value: any, data: StatType) => JSX.Element | string | undefined | ReactElement | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  csvRenderer?: (value: any, data: StatType) => string | undefined | number;
  isSticky?: boolean;
}

interface TableConfigProps {
  tableType: TableType;
  previewAd: (insertionOrderId: string, isSingleFamily: boolean) => void;
  onPropertyClick: (listingAlias: string, propertyName: string) => void;
  downloadAd?: (insertionOrderId: string, isSingleFamily: boolean, propertyName: string) => void;
  downloadingIds?: Set<string>;
}

const propertyColumn = (onPropertyClick: (listingAlias: string, propertyName: string) => void): AdSpendTableConfig => ({
  field: 'propertyDetails',
  header: 'Property',
  align: 'left',
  renderer: (propertyDetails: PortfolioAnalyticsPropertyDto, data: StatType) => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
      <TextButton
        css={{ textAlign: 'left', display: 'block' }}
        onClick={() => onPropertyClick(data.currentListingAlias, propertyDetails.listingName)}
      >
        <Paragraph css={{ whiteSpace: 'nowrap', textDecoration: 'underline' }}>{propertyDetails.listingName}</Paragraph>
        <Paragraph css={{ whiteSpace: 'nowrap' }}>
          {propertyDetails.city} {propertyDetails.state}
        </Paragraph>
      </TextButton>
      {data.totalDaysSpotlighted > 0 && data.localDatesSpotlighted && (
        <Box onClick={(e) => e.stopPropagation()}>
          <Popover
            tone="neutral"
            header={<Text textStyle="body-sm-bold">Listing Spotlight</Text>}
            body={
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {formatSpotlightDates(data.localDatesSpotlighted).map((item, index) => (
                  <Text key={index} textStyle="body-sm">
                    {`${item.range} (${item.count} day${item.count > 1 ? 's' : ''})`}
                  </Text>
                ))}
              </Box>
            }
            trigger={
              <Tag
                css={{
                  textTransform: 'capitalize',
                  cursor: 'pointer',
                }}
                size="md"
                tone="blue"
              >
                Spotlight
              </Tag>
            }
          />
        </Box>
      )}
    </Box>
  ),
  csvRenderer: (propertyDetails: PortfolioAnalyticsPropertyDto) =>
    `${propertyDetails.listingName} ${propertyDetails.city} ${propertyDetails.state}`,
});

const marketTrendsColumn: AdSpendTableConfig = {
  header: 'Market Trends',
  align: 'left',
  renderer: (_: unknown, data: StatType) => {
    if (data.isSingleFamily) return '-';
    if (!data.hasMoodysData) return 'Coming soon';
    return <MarketTrendsDownloadCell listingAlias={data.propertyDetails.listingAlias} />;
  },
  csvRenderer: () => '',
};

export const makeTableConfig = ({
  tableType,
  previewAd,
  onPropertyClick,
  downloadAd,
  downloadingIds,
}: TableConfigProps): Array<AdSpendTableConfig> => {
  if (tableType === TableType.ALL) {
    return [
      propertyColumn(onPropertyClick),
      {
        header: 'Package',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const adproductTypes = data.adproductTypes;
          const packageNames = adproductTypes.map((productType) => {
            const listingType = data.allListingFilterTypes.find((type) => type !== 'PAID');
            return mapPackageTypeName(productType, listingType);
          });
          const hasExtend = data.addOnBoostCost > 0 || data.addOnImpressions > 0;
          if (hasExtend && packageNames.length > 0) {
            return `${packageNames.join(', ')} + Extend`;
          }
          return packageNames.join(', ');
        },
        csvRenderer: (_: unknown, data: StatType) => {
          const adproductTypes = data.adproductTypes;
          const packageNames = adproductTypes.map((productType) => {
            const listingType = data.allListingFilterTypes.find((type) => type !== 'PAID');
            return mapPackageTypeName(productType, listingType);
          });
          const hasExtend = data.addOnBoostCost > 0 || data.addOnImpressions > 0;
          if (hasExtend && packageNames.length > 0) {
            return `${packageNames.join(', ')} + Extend`;
          }
          return packageNames.join(', ');
        },
      },
      {
        header: 'Impressions',
        align: 'left',
        renderer: (_: unknown, data: StatType) => formatNumber(data.impressionCount + data.addOnImpressions),
        csvRenderer: (_: unknown, data: StatType) => formatNumber(data.impressionCount + data.addOnImpressions),
      },
      {
        field: 'emailLeadCount',
        header: 'Email leads',
        align: 'left',
        renderer: (emailLeadCount: number) => formatNumber(emailLeadCount),
        csvRenderer: (emailLeadCount: number) => formatNumber(emailLeadCount),
      },
      {
        field: 'phoneLeadCount',
        header: 'Phone leads',
        align: 'left',
        renderer: (phoneLeadCount: number) => formatNumber(phoneLeadCount),
        csvRenderer: (phoneLeadCount: number) => formatNumber(phoneLeadCount),
      },
      {
        field: 'listingWebReferrals',
        header: 'Listing web referrals',
        align: 'left',
        renderer: (listingWebReferrals: number) => formatNumber(listingWebReferrals ?? 0),
        csvRenderer: (listingWebReferrals: number) => formatNumber(listingWebReferrals ?? 0),
      },
      {
        field: 'coreProductCost',
        header: 'Core spend',
        align: 'left',
        renderer: (coreProductCost: number) => (isNaN(coreProductCost) ? '-' : formatCurrency(coreProductCost)),
        csvRenderer: (coreProductCost: number) => (isNaN(coreProductCost) ? '-' : formatCurrency(coreProductCost)),
      },
      {
        field: 'boostCost',
        header: 'Boost spend',
        align: 'left',
        renderer: (boostCost: number) => (isNaN(boostCost) ? '-' : formatCurrency(boostCost)),
        csvRenderer: (boostCost: number) => (isNaN(boostCost) ? '-' : formatCurrency(boostCost)),
      },
      {
        field: 'addOnBoostCost',
        header: 'Extend spend',
        align: 'left',
        renderer: (addOnBoostCost: number) => (isNaN(addOnBoostCost) ? '-' : formatCurrency(addOnBoostCost)),
        csvRenderer: (addOnBoostCost: number) => (isNaN(addOnBoostCost) ? '-' : formatCurrency(addOnBoostCost)),
      },
      marketTrendsColumn,
      {
        field: 'insertionOrderId',
        header: 'Preview',
        align: 'left',
        renderer: (insertionOrderId: string, data: StatType) =>
          insertionOrderId ? (
            <TextButton css={{ whiteSpace: 'nowrap' }} onClick={() => previewAd(insertionOrderId, data.isSingleFamily)}>
              View ads
            </TextButton>
          ) : (
            '-'
          ),
        csvRenderer: () => '',
      },
      ...(downloadAd
        ? [
            {
              field: 'insertionOrderId' as keyof StatType,
              header: 'Download',
              align: 'left' as const,
              renderer: (insertionOrderId: string, data: StatType) => {
                if (!insertionOrderId) return '-';
                if (downloadingIds?.has(insertionOrderId)) return <Spinner size="sm" />;
                return (
                  <TextButton
                    css={{ whiteSpace: 'nowrap' }}
                    onClick={() => downloadAd(insertionOrderId, data.isSingleFamily, data.propertyDetails.listingName)}
                  >
                    Download
                  </TextButton>
                );
              },
              csvRenderer: () => '',
            },
          ]
        : []),
      {
        field: 'totalCost',
        header: 'Total spend',
        align: 'left',
        renderer: (totalCost: number) => (isNaN(totalCost) ? '-' : formatCurrency(totalCost)),
        csvRenderer: (totalCost: number) => (isNaN(totalCost) ? '-' : formatCurrency(totalCost)),
        isSticky: true,
      },
    ];
  }

  if (tableType === TableType.PACKAGES) {
    return [
      propertyColumn(onPropertyClick),
      {
        header: 'Package',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const adproductTypes = data.adproductTypes;
          return adproductTypes
            .map((productType) => {
              const listingType = data.allListingFilterTypes.find((type) => type !== 'PAID');
              return mapPackageTypeName(productType, listingType);
            })
            .join(', ');
        },
        csvRenderer: (_: unknown, data: StatType) => {
          const adproductTypes = data.adproductTypes;
          return adproductTypes
            .map((productType) => {
              const listingType = data.allListingFilterTypes.find((type) => type !== 'PAID');
              return mapPackageTypeName(productType, listingType);
            })
            .join(', ');
        },
      },
      {
        field: 'impressionCount',
        header: 'Impressions',
        align: 'left',
        renderer: (impressionCount: number) => formatNumber(impressionCount),
        csvRenderer: (impressionCount: number) => formatNumber(impressionCount),
      },
      {
        field: 'boostClickCount',
        header: 'Ad clicks',
        align: 'left',
        renderer: (boostClickCount: number) => formatNumber(boostClickCount),
        csvRenderer: (boostClickCount: number) => formatNumber(boostClickCount),
      },
      {
        field: 'listingWebReferrals',
        header: 'Listing web referrals',
        align: 'left',
        renderer: (listingWebReferrals: number) => formatNumber(listingWebReferrals ?? 0),
        csvRenderer: (listingWebReferrals: number) => formatNumber(listingWebReferrals ?? 0),
      },
      {
        field: 'coreProductCost',
        header: 'Core spend',
        align: 'left',
        renderer: (coreProductCost: number) => (isNaN(coreProductCost) ? '-' : formatCurrency(coreProductCost)),
        csvRenderer: (coreProductCost: number) => (isNaN(coreProductCost) ? '-' : formatCurrency(coreProductCost)),
      },
      marketTrendsColumn,
      {
        field: 'insertionOrderId',
        header: 'Preview',
        align: 'left',
        renderer: (insertionOrderId: string, data: StatType) =>
          insertionOrderId ? (
            <TextButton css={{ whiteSpace: 'nowrap' }} onClick={() => previewAd(insertionOrderId, data.isSingleFamily)}>
              View ads
            </TextButton>
          ) : (
            '-'
          ),
        csvRenderer: () => '',
      },
      ...(downloadAd
        ? [
            {
              field: 'insertionOrderId' as keyof StatType,
              header: 'Download',
              align: 'left' as const,
              renderer: (insertionOrderId: string, data: StatType) => {
                if (!insertionOrderId) return '-';
                if (downloadingIds?.has(insertionOrderId)) return <Spinner size="sm" />;
                return (
                  <TextButton
                    css={{ whiteSpace: 'nowrap' }}
                    onClick={() => downloadAd(insertionOrderId, data.isSingleFamily, data.propertyDetails.listingName)}
                  >
                    Download
                  </TextButton>
                );
              },
              csvRenderer: () => '',
            },
          ]
        : []),
    ];
  }

  if (tableType === TableType.EXTEND) {
    return [
      propertyColumn(onPropertyClick),
      {
        header: 'Platform',
        align: 'left',
        renderer: (_: unknown, data: StatType) => getExtendPlatformLabel(data),
        csvRenderer: (_: unknown, data: StatType) => getExtendPlatformLabel(data),
      },
      {
        header: 'Preview',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const ioId = data.addOnInsertionOrderId || data.insertionOrderId;
          return ioId ? (
            <TextButton css={{ whiteSpace: 'nowrap' }} onClick={() => previewAd(ioId, data.isSingleFamily)}>
              View ads
            </TextButton>
          ) : (
            'Not available'
          );
        },
        csvRenderer: () => '',
      },
      {
        field: 'addOnImpressions',
        header: 'Total impressions',
        align: 'left',
        renderer: (addOnImpressions: number) => formatNumber(addOnImpressions),
        csvRenderer: (addOnImpressions: number) => formatNumber(addOnImpressions),
      },
      {
        field: 'addOnClickCount',
        header: 'Total clicks',
        align: 'left',
        renderer: (addOnClickCount: number) => formatNumber(addOnClickCount),
        csvRenderer: (addOnClickCount: number) => formatNumber(addOnClickCount),
      },
      {
        header: 'Total CTR',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const ctr = data.addOnImpressions > 0 ? data.addOnClickCount / data.addOnImpressions : 0;
          return `${formatNumber(ctr * 100)}%`;
        },
        csvRenderer: (_: unknown, data: StatType) => {
          const ctr = data.addOnImpressions > 0 ? data.addOnClickCount / data.addOnImpressions : 0;
          return `${formatNumber(ctr * 100)}%`;
        },
      },
      {
        field: 'addOnMetaImpressions',
        header: 'Meta impressions',
        align: 'left',
        renderer: (addOnMetaImpressions: number) => formatNumber(addOnMetaImpressions),
        csvRenderer: (addOnMetaImpressions: number) => formatNumber(addOnMetaImpressions),
      },
      {
        field: 'addOnMetaClicks',
        header: 'Meta clicks',
        align: 'left',
        renderer: (addOnMetaClicks: number) => formatNumber(addOnMetaClicks),
        csvRenderer: (addOnMetaClicks: number) => formatNumber(addOnMetaClicks),
      },
      {
        header: 'Meta CTR',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const ctr = data.addOnMetaImpressions > 0 ? data.addOnMetaClicks / data.addOnMetaImpressions : 0;
          return `${formatNumber(ctr * 100)}%`;
        },
        csvRenderer: (_: unknown, data: StatType) => {
          const ctr = data.addOnMetaImpressions > 0 ? data.addOnMetaClicks / data.addOnMetaImpressions : 0;
          return `${formatNumber(ctr * 100)}%`;
        },
      },
      {
        field: 'addOnTiktokImpressions',
        header: 'TikTok impressions',
        align: 'left',
        renderer: (addOnTiktokImpressions: number) => formatNumber(addOnTiktokImpressions),
        csvRenderer: (addOnTiktokImpressions: number) => formatNumber(addOnTiktokImpressions),
      },
      {
        field: 'addOnTiktokClicks',
        header: 'TikTok clicks',
        align: 'left',
        renderer: (addOnTiktokClicks: number) => formatNumber(addOnTiktokClicks),
        csvRenderer: (addOnTiktokClicks: number) => formatNumber(addOnTiktokClicks),
      },
      {
        header: 'TikTok CTR',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const ctr = data.addOnTiktokImpressions > 0 ? data.addOnTiktokClicks / data.addOnTiktokImpressions : 0;
          return `${formatNumber(ctr * 100)}%`;
        },
        csvRenderer: (_: unknown, data: StatType) => {
          const ctr = data.addOnTiktokImpressions > 0 ? data.addOnTiktokClicks / data.addOnTiktokImpressions : 0;
          return `${formatNumber(ctr * 100)}%`;
        },
      },
      {
        field: 'addOnBoostCost',
        header: 'Contract spend',
        align: 'left',
        renderer: (addOnBoostCost: number) => (isNaN(addOnBoostCost) ? '-' : formatCurrency(addOnBoostCost)),
        csvRenderer: (addOnBoostCost: number) => (isNaN(addOnBoostCost) ? '-' : formatCurrency(addOnBoostCost)),
        isSticky: true,
      },
      {
        header: 'Cost per click',
        align: 'left',
        renderer: (_: unknown, data: StatType) => {
          const cpc = data.addOnClickCount > 0 ? data.addOnBoostCost / data.addOnClickCount : 0;
          return cpc > 0 ? formatCurrency(cpc) : '-';
        },
        csvRenderer: (_: unknown, data: StatType) => {
          const cpc = data.addOnClickCount > 0 ? data.addOnBoostCost / data.addOnClickCount : 0;
          return cpc > 0 ? formatCurrency(cpc) : '-';
        },
        isSticky: true,
      },
      marketTrendsColumn,
    ];
  }

  return [
    propertyColumn(onPropertyClick),
    {
      field: 'cpmBoostTypes',
      header: 'Package',
      align: 'left',
      renderer: (cpmBoostTypes: Array<ClientCpmBoostType>) => getCpmBoostPlatformLabel(cpmBoostTypes),
      csvRenderer: (cpmBoostTypes: Array<ClientCpmBoostType>) => getCpmBoostPlatformLabel(cpmBoostTypes),
    },
    {
      field: 'cpmBoostTypes',
      header: 'Sponsored ads',
      align: 'center',
      renderer: (cpmBoostTypes: Array<ClientCpmBoostType>) => (
        <Icon size="md" css={{ display: 'block', margin: 'auto' }}>
          {cpmBoostTypes.some((type) => type === ClientCpmBoostType.ZG) ? <IconCheckmarkFilled /> : <IconCloseFilled />}
        </Icon>
      ),
      csvRenderer: (cpmBoostTypes: Array<ClientCpmBoostType>) =>
        cpmBoostTypes.some((type) => type === ClientCpmBoostType.ZG) ? 'Yes' : 'No',
    },
    {
      field: 'cpmBoostTypes',
      header: 'Meta ads',
      align: 'center',
      renderer: (cpmBoostTypes: Array<ClientCpmBoostType>) => (
        <Icon size="md" css={{ display: 'block', margin: 'auto' }}>
          {cpmBoostTypes.some(
            (type) => type === ClientCpmBoostType.FACEBOOK || type === ClientCpmBoostType.INSTAGRAM,
          ) ? (
            <IconCheckmarkFilled />
          ) : (
            <IconCloseFilled />
          )}
        </Icon>
      ),
      csvRenderer: (cpmBoostTypes: Array<ClientCpmBoostType>) =>
        cpmBoostTypes.some((type) => type === ClientCpmBoostType.FACEBOOK || type === ClientCpmBoostType.INSTAGRAM)
          ? 'Yes'
          : 'No',
    },
    {
      field: 'boostClickCount',
      header: 'Ad clicks',
      align: 'left',
      renderer: (boostClickCount: number) => formatNumber(boostClickCount),
      csvRenderer: (boostClickCount: number) => formatNumber(boostClickCount),
    },
    {
      field: 'boostCost',
      header: 'Contracted spend',
      align: 'left',
      renderer: (value: number) => (isNaN(value) ? '-' : formatCurrency(value)),
      csvRenderer: (value: number) => (isNaN(value) ? '-' : formatCurrency(value)),
    },
    {
      field: 'boostImpressions',
      header: 'Impressions',
      align: 'left',
      renderer: (boostImpressions: number) => formatNumber(boostImpressions),
      csvRenderer: (boostImpressions: number) => formatNumber(boostImpressions),
    },
    {
      field: 'boostCpc',
      header: 'Cost per click',
      align: 'left',
      renderer: (boostCpc: number) => (isNaN(boostCpc) ? '-' : formatCurrency(boostCpc)),
      csvRenderer: (boostCpc: number) => (isNaN(boostCpc) ? '-' : formatCurrency(boostCpc)),
    },
    {
      field: 'boostCtr',
      header: 'CTR',
      align: 'left',
      renderer: (boostCtr: number) => `${formatNumber(boostCtr * 100)}%`,
      csvRenderer: (boostCtr: number) => `${formatNumber(boostCtr * 100)}%`,
    },
    marketTrendsColumn,
  ];
};
